/**
 * Runtime configuration for the Ontology UI.
 *
 * Reads window.__ONTOLOGY_CONFIG__ set by public/config.js at load time.
 * The PROXY_BASE_URL value is used as the prefix for all API calls so that
 * deployments on a different domain can point to the correct proxy server.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface OntologyRuntimeConfig {
  PROXY_BASE_URL: string
  ONTOLOGY_API_SERVICE_URL: string
  DASHBOARD_SANDBOX_URL: string
  IDENTITY_API_URL: string
  DEBUG_MODE: boolean
  /**
   * Frontend perf telemetry (AXI-3875): Web Vitals + per-route TTID/TTFD
   * beacons to the api-service collector. Opt-out — defaults to enabled when
   * the key is absent. Set to `false` to silence the beacons entirely (the
   * collector itself is already a no-op when the api-service has no OTLP
   * endpoint configured).
   */
  PERF_TELEMETRY_ENABLED: boolean
  /**
   * Product usage analytics (page views, heartbeats, explicit actions) posted
   * to the proxy `POST /api/v1/usage-events`. Opt-out — defaults to enabled
   * when the key is absent. Set to `false` to silence the tracker.
   */
  USAGE_ANALYTICS_ENABLED: boolean
  /**
   * Whether the identity domain (login screen + auth route-guard) is required.
   * Opt-in — defaults to disabled when the key is absent. Only an explicit
   * `true` turns identity on; this lets the new build deploy to older
   * environments that have no identity service yet (login would have nothing to
   * authenticate against there). Environments that run identity must stamp this
   * to `true`.
   */
  IDENTITY_ENABLED: boolean | string
  /**
   * Guided-demo fixture mode (apps/prophet-demo). Opt-in — only an explicit
   * `true` swaps the RTK base query and the agent SSE client for the canned
   * in-browser fixtures under `state/src/demo/`, so the whole UI runs with no
   * proxy, api-service, or identity backend. Never stamp this on a real
   * deployment.
   */
  DEMO_MODE: boolean
}

const w = (typeof window === 'undefined' ? {} : window) as unknown as Record<string, any>

const runtimeCfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>

function normalizeBaseUrl(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

const proxyBase = normalizeBaseUrl(runtimeCfg.PROXY_BASE_URL ?? '')
const ontologyServiceBase = normalizeBaseUrl(runtimeCfg.ONTOLOGY_API_SERVICE_URL ?? '')
const dashboardSandboxBase = normalizeBaseUrl(runtimeCfg.DASHBOARD_SANDBOX_URL ?? '')
const identityApiBase = normalizeBaseUrl(runtimeCfg.IDENTITY_API_URL ?? '')

export function getProxyBaseUrl(): string {
  return proxyBase
}

export function getOntologyApiBaseUrl(): string {
  return `${proxyBase}/api/v1/ontology`
}

export function getOntologyServiceBaseUrl(): string {
  return `${ontologyServiceBase}/api/ontology`
}

/**
 * Collector endpoint for frontend perf beacons (Web Vitals + TTID/TTFD).
 * Lives on the api-service under the unauthenticated `/api/ontology/*` prefix
 * so `navigator.sendBeacon` (which cannot set an `X-API-Key` header) reaches
 * it. Same host as the other ontology-service calls.
 */
export function getTelemetryCollectorUrl(): string {
  return `${ontologyServiceBase}/api/ontology/telemetry`
}

// Read on every call so config edits applied at runtime (e.g. in tests) take
// effect without re-importing this module. Opt-out: only an explicit `false`
// disables telemetry, so deployments that predate the key keep it on.
export function isPerfTelemetryEnabled(): boolean {
  const cfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>
  return cfg.PERF_TELEMETRY_ENABLED !== false
}

/** Authenticated product-usage ingest endpoint on the proxy. */
export function getUsageEventsUrl(): string {
  return proxyBase ? `${proxyBase}/api/v1/usage-events` : '/api/v1/usage-events'
}

// Opt-out: only an explicit `false` disables usage analytics.
export function isUsageAnalyticsEnabled(): boolean {
  const cfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>
  return cfg.USAGE_ANALYTICS_ENABLED !== false
}

export function getConverseStreamUrl(): string {
  return `${proxyBase}/api/v1/converse-stream`
}

// Base URL of the identity API server (axilon-api /api/v2 sessions/users),
// consumed by the identity domain's platform services (getApiServerUrl).
export function getIdentityApiUrl(): string {
  return identityApiBase
}

// URL of the cross-origin dashboard sandbox app (apps/dashboard-sandbox).
// The Dashboards page loads this in an iframe so untrusted user TSX
// renders on a different origin than the main UI. Empty string means
// the sandbox is not configured and the render pane will display a
// helpful placeholder instead.
//
// Keep a trailing slash: S3/CloudFront serve the sandbox under `/sandbox/`
// (object keys `sandbox/` + `sandbox/index.html`). Without the slash,
// `/sandbox` 404s and CloudFront's SPA fallback returns the main app
// index — which then renders NotFound inside the iframe.
export function getDashboardSandboxUrl(): string {
  if (!dashboardSandboxBase) return ''
  return dashboardSandboxBase.endsWith('/') ? dashboardSandboxBase : `${dashboardSandboxBase}/`
}

// Read on every call so config edits applied at runtime (e.g. in tests) take
// effect without re-importing this module. Defaults to false when the key is
// missing so production deployments are opt-in.
export function isDebugModeConfigEnabled(): boolean {
  const cfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>
  return cfg.DEBUG_MODE === true
}

// Read on every call so config edits applied at runtime (e.g. in tests) take
// effect without re-importing this module. Opt-in: defaults to false when the
// key is missing, so older deployments without an identity service ship without
// requiring login. Runtime config is normally JSON-stamped as a boolean, but
// local/development launchers can inject environment values as strings.
export function isIdentityEnabled(): boolean {
  const cfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>
  return (
    cfg.IDENTITY_ENABLED === true ||
    (typeof cfg.IDENTITY_ENABLED === 'string' &&
      cfg.IDENTITY_ENABLED.trim().toLowerCase() === 'true')
  )
}

// Read on every call so config edits applied at runtime (e.g. in tests) take
// effect without re-importing this module. Opt-in: defaults to false when the
// key is missing so real deployments can never drift into fixture mode.
export function isDemoMode(): boolean {
  const cfg = (w.__ONTOLOGY_CONFIG__ ?? {}) as Partial<OntologyRuntimeConfig>
  return cfg.DEMO_MODE === true
}
